import { Box, IconButton, Typography } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { RichTextEditor, RichTextReadOnly } from '@/components/common/RichTextEditor';
import { stripHtml } from '@/utils/stripHtml';
import type { ReadOnlyVariant } from '@/auth/sectionReadOnlyStyle';

interface SelfAssessmentRowError {
  developmentNeed?: boolean;
  appraiseeResponse?: boolean;
}

interface SelfAssessmentRowProps {
  mode: 'A' | 'B';
  developmentNeed: string;
  actionToResolve: string;
  appraiseeResponse: string;
  onChange: (field: 'developmentNeed' | 'appraiseeResponse', value: string) => void;
  onBlur: () => void;
  onDelete: () => void;
  onDeleteMouseDown?: () => void;
  canEdit: boolean;
  canDelete: boolean;
  error?: SelfAssessmentRowError;
  readOnlyVariant?: ReadOnlyVariant;
  striped?: boolean;
}

const errorFieldSx = {
  border: '2px solid',
  borderColor: 'error.main',
  borderRadius: 1,
} as const;

export function SelfAssessmentRow({
  mode,
  developmentNeed,
  actionToResolve,
  appraiseeResponse,
  onChange,
  onBlur,
  onDelete,
  onDeleteMouseDown,
  canEdit,
  canDelete,
  error,
  readOnlyVariant = 'boxed',
  striped = false,
}: SelfAssessmentRowProps) {
  const responseError = !!error?.appraiseeResponse;
  const developmentNeedError = !!error?.developmentNeed;
  if (mode === 'A') {
    return (
      <Box
        data-testid="self-assessment-row"
        data-striped={striped || undefined}
        sx={{
          display: 'flex',
          gap: 2,
          alignItems: 'flex-start',
          p: 1.5,
          borderRadius: 1,
          mb: 2,
          ...(striped && { bgcolor: 'custom.tableRowStripe' }),
        }}
      >
        <Box sx={{ flex: 1 }}>
          {stripHtml(developmentNeed) && (
            <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#0D47A1' }}>
              Identified need
            </Typography>
          )}
          <RichTextReadOnly content={developmentNeed} variant="plain" />
          {stripHtml(actionToResolve) && (
            <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#0D47A1', mt: 1 }}>
              Agreed actions
            </Typography>
          )}
          <RichTextReadOnly content={actionToResolve} variant="plain" />
        </Box>
        <Box
          sx={{ flex: 1, ...(responseError && errorFieldSx) }}
          data-testid={responseError ? 'field-error' : undefined}
        >
          {canEdit ? (
            <RichTextEditor
              content={appraiseeResponse}
              onChange={(html) => onChange('appraiseeResponse', html)}
              onBlur={onBlur}
              placeholder="Enter your response..."
            />
          ) : (
            <RichTextReadOnly content={appraiseeResponse} variant="plain" />
          )}
        </Box>
      </Box>
    );
  }

  // Mode B
  return (
    <Box
      data-testid="self-assessment-row"
      data-striped={striped || undefined}
      sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', mb: 2 }}
    >
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          gap: 2,
          p: 1.5,
          borderRadius: 1,
          ...(striped && { bgcolor: 'custom.tableRowStripe' }),
        }}
      >
        <Box
          sx={{ flex: 1, ...(developmentNeedError && errorFieldSx) }}
          data-testid={developmentNeedError ? 'field-error' : undefined}
        >
          {canEdit ? (
            <RichTextEditor
              content={developmentNeed}
              onChange={(html) => onChange('developmentNeed', html)}
              onBlur={onBlur}
              placeholder="Enter development need..."
            />
          ) : (
            <RichTextReadOnly content={developmentNeed} variant={readOnlyVariant} />
          )}
        </Box>
        <Box
          sx={{ flex: 1, ...(responseError && errorFieldSx) }}
          data-testid={responseError ? 'field-error' : undefined}
        >
          {canEdit ? (
            <RichTextEditor
              content={appraiseeResponse}
              onChange={(html) => onChange('appraiseeResponse', html)}
              onBlur={onBlur}
              placeholder="Enter your response..."
            />
          ) : (
            <RichTextReadOnly content={appraiseeResponse} variant={readOnlyVariant} />
          )}
        </Box>
      </Box>
      {canEdit && (
        <IconButton
          onMouseDown={onDeleteMouseDown}
          onClick={onDelete}
          color="error"
          size="small"
          disabled={!canDelete}
          aria-label="Delete development need"
        >
          <DeleteOutlineIcon />
        </IconButton>
      )}
    </Box>
  );
}

export default SelfAssessmentRow;
